//
// # Wire Follow Planning
//
// One answer to "an entity moved by `delta`; what should happen to the wires
// attached to it". Pure: it takes plain endpoint pairs and point keys and
// returns a list of edits, so both drag modes can share it and both can be
// tested without an Editor.
//
// Two implementations previously disagreed here. `DragWithWires` (single
// entity) re-routed on commit, producing clean orthogonal geometry.
// `MoveSelection` (multi-selection) only translated endpoints, so any
// off-axis drag left diagonal wires behind — a state the rest of the editor
// cannot handle, since hit-testing and connectivity extraction both run
// through `Manhattan.calc` and give up on a diagonal. Which one you got
// depended on selection state at mousedown, not on the gesture.
//
// The rules, in one place:
//
//   - A network attached only to moving pins translates rigidly. Its shape is
//     deliberate and nothing anchors it, so re-routing would scramble
//     hand-drawn geometry for no reason.
//   - A network anchored to something stationary is re-routed: the run from
//     each moving pin to its first branch point or stationary anchor is
//     replaced by a minimum-bend orthogonal path.
//   - An endpoint sitting on a stationary pin never moves, even when its wire
//     is itself selected. Dragging a selection that includes a wire but not
//     the instance it connects to used to tear the wire off its pin.
//

import { Point, model } from "GenAlphaModel";
import { buildConnectivity, segmentsConnect } from "./connectivity";
import { ManhattanRouter, pathToSegments } from "./manhattan_router";
import { pointKey } from "./wire_utils";

// A wire, as this module sees it: two endpoints and nothing else. Callers map
// indices back to their own segment objects.
export interface PlannedWire {
  start: Point;
  end: Point;
}

// What to do with the wires. `index` refers to the input `wires` array.
export type WireEdit =
  | { kind: "update"; index: number; endpoints: [Point, Point] }
  | { kind: "create"; endpoints: [Point, Point] }
  | { kind: "delete"; index: number };

export interface PlanInput {
  // Every wire in the schematic.
  wires: PlannedWire[];
  // Point keys of pins that move with this gesture.
  movingPinKeys: Set<string>;
  // The same pins as locations, so the planner knows where they land. A route
  // that happens to come to rest on another moving pin's new position shorts
  // that pin onto the net. Optional; without it that check is skipped.
  movingPins?: Point[];
  // Point keys of every other pin. An endpoint here is anchored.
  staticPinKeys: Set<string>;
  // Indices of wires the user selected outright, which move with the gesture
  // even where no pin drags them.
  selectedWireIndices?: Set<number>;
  // How far the moving entities travelled.
  delta: Point;
  // Instance boxes a re-route should prefer not to cross.
  obstacles?: model.Bbox[];
  // The editor's grid snap, for the one case that has to invent a coordinate:
  // the crossover of a detour around a pin. Everything else is built from
  // coordinates the caller already snapped. Optional — without it a detour lands
  // wherever the geometry puts it, which is legal but can sit off-grid.
  snap?: (pt: Point) => Point;
}

// Is `pt` on `wire`, strictly between its endpoints?
function interiorContains(wire: PlannedWire, pt: Point): boolean {
  const { start: s, end: e } = wire;
  if (s.x === e.x) {
    if (s.y === e.y || pt.x !== s.x) return false;
    return pt.y > Math.min(s.y, e.y) && pt.y < Math.max(s.y, e.y);
  }
  if (s.y === e.y) {
    if (pt.y !== s.y) return false;
    return pt.x > Math.min(s.x, e.x) && pt.x < Math.max(s.x, e.x);
  }
  return false;
}

// Connected components of the wire graph.
//
// Wires join at shared endpoints, and also where one wire's endpoint lands in
// another's interior — a mid-span T. Joining on endpoints alone put the stem
// of such a T in its own component, so it looked unanchored and translated
// rigidly off the trunk it was attached to.
//
// Exported so the live drag preview partitions wires exactly as the plan that
// lands on release does. Two notions of "connected" meant a stub hanging off a
// mid-span junction translated rigidly under the cursor, visibly leaving the
// dot behind, and only snapped back when the real plan ran.
export function components(wires: PlannedWire[]): number[][] {
  const byPoint = new Map<string, number[]>();
  const byRow = new Map<number, number[]>();
  const byCol = new Map<number, number[]>();
  const bucket = (map: Map<number, number[]>, key: number, i: number) => {
    const existing = map.get(key);
    if (existing) existing.push(i);
    else map.set(key, [i]);
  };

  for (let i = 0; i < wires.length; i++) {
    const { start, end } = wires[i];
    for (const key of [pointKey(start), pointKey(end)]) {
      const existing = byPoint.get(key);
      if (existing) existing.push(i);
      else byPoint.set(key, [i]);
    }
    if (start.x === end.x && start.y !== end.y) bucket(byCol, start.x, i);
    else if (start.y === end.y && start.x !== end.x) bucket(byRow, start.y, i);
  }

  // Wires whose interior contains `pt`.
  const spanning = (pt: Point): number[] => {
    const out: number[] = [];
    for (const i of byRow.get(pt.y) ?? []) {
      if (interiorContains(wires[i], pt)) out.push(i);
    }
    for (const i of byCol.get(pt.x) ?? []) {
      if (interiorContains(wires[i], pt)) out.push(i);
    }
    return out;
  };

  // Adjacency, built symmetrically up front.
  //
  // A mid-span T is only visible from one side: walking out from the stem's
  // endpoint finds the trunk, but walking the trunk's endpoints never finds
  // the stem. Discovering neighbours on the fly therefore traversed that link
  // in one direction only — and since the flood fill marks nodes seen
  // globally, whichever wire happened to come first in the array claimed its
  // component and the other was stranded in one of its own. It depended on
  // wire ordering, which is why the resulting misbehaviour came and went.
  const adjacent = new Map<number, Set<number>>();
  const link = (a: number, b: number) => {
    if (a === b) return;
    const forward = adjacent.get(a);
    if (forward) forward.add(b);
    else adjacent.set(a, new Set([b]));
    const back = adjacent.get(b);
    if (back) back.add(a);
    else adjacent.set(b, new Set([a]));
  };
  for (let i = 0; i < wires.length; i++) {
    for (const end of [wires[i].start, wires[i].end]) {
      for (const j of byPoint.get(pointKey(end)) ?? []) link(i, j);
      for (const j of spanning(end)) link(i, j);
    }
  }

  const seen = new Set<number>();
  const out: number[][] = [];
  for (let seed = 0; seed < wires.length; seed++) {
    if (seen.has(seed)) continue;
    const group: number[] = [];
    const queue = [seed];
    seen.add(seed);
    for (let i = queue.pop(); i !== undefined; i = queue.pop()) {
      group.push(i);
      for (const neighbor of adjacent.get(i) ?? []) {
        if (seen.has(neighbor)) continue;
        seen.add(neighbor);
        queue.push(neighbor);
      }
    }
    out.push(group);
  }
  return out;
}

// The point nearest `from` where another wire ends inside `seg`'s span, or
// null if nothing branches off its middle.
function nearestInteriorBranch(
  wires: PlannedWire[],
  index: number,
  from: Point
): Point | null {
  const seg = wires[index];
  let best: Point | null = null;
  let bestDist = Infinity;
  for (let j = 0; j < wires.length; j++) {
    if (j === index) continue;
    for (const end of [wires[j].start, wires[j].end]) {
      if (!interiorContains(seg, end)) continue;
      const dist = Math.abs(end.x - from.x) + Math.abs(end.y - from.y);
      if (dist < bestDist) {
        bestDist = dist;
        best = end;
      }
    }
  }
  return best;
}

// Walk the linear run leaving `from`, stopping at the first branch point or
// stationary anchor. Returns the segments crossed and where it stopped.
//
// `isStop` decides; the start point is exempt so a run can leave a pin that is
// itself a branch.
//
// A branch does not have to sit at a segment's endpoint. Where another wire
// ends part-way along one, the run stops there too — otherwise it would
// swallow the whole segment and carry it away from the wire hanging off its
// middle, silently detaching that branch. In that case `remainder` is the
// stretch beyond the branch, which the caller must re-create: the segment
// itself is consumed by the re-route.
function traceRun(
  from: Point,
  wires: PlannedWire[],
  candidates: number[],
  taken: Set<number>,
  isStop: (pt: Point) => boolean
): {
  run: number[];
  farPoint: Point | null;
  remainder: [Point, Point] | null;
} {
  const run: number[] = [];
  let farPoint: Point | null = null;
  let remainder: [Point, Point] | null = null;
  let at = from;
  const visited = new Set<string>([pointKey(from)]);

  for (;;) {
    // The next unclaimed segment leaving `at`.
    const nextIndex = candidates.find((i) => {
      if (taken.has(i)) return false;
      return Point.eq(wires[i].start, at) || Point.eq(wires[i].end, at);
    });
    if (nextIndex === undefined) break;

    taken.add(nextIndex);
    run.push(nextIndex);
    const wire = wires[nextIndex];
    const onward = Point.eq(wire.start, at) ? wire.end : wire.start;

    // Something branching off this segment's middle stops the run there, and
    // the stretch beyond it has to survive as its own segment.
    const branch = nearestInteriorBranch(wires, nextIndex, at);
    if (branch) {
      farPoint = branch;
      remainder = [branch, onward];
      break;
    }

    const onwardKey = pointKey(onward);
    if (visited.has(onwardKey)) break; // a loop; stop rather than circle
    visited.add(onwardKey);
    at = onward;

    if (isStop(at)) {
      farPoint = at;
      break;
    }
  }

  return { run, farPoint, remainder };
}

// Where the re-routed run should actually meet the rest of the net.
//
// The far point is captured before the move. Routing back to it makes the run
// travel along whatever wire it branches from, laying a redundant segment on
// top of that wire: invisible, but it turns the old junction into a spurious
// dot and leaves two collinear segments overlapping. When the branch is onto a
// wire that stays put, the run should instead meet that wire where it now is —
// straight across, no return leg.
//
// A far point that is a stationary *pin* is never relocated: the route has to
// land on the pin.
function meetingPoint(
  near: Point,
  farPoint: Point,
  wires: PlannedWire[],
  run: Set<number>,
  staticPinKeys: Set<string>
): { point: Point; host: number | null } {
  if (staticPinKeys.has(pointKey(farPoint))) {
    return { point: farPoint, host: null };
  }

  const clamp = (v: number, lo: number, hi: number) =>
    Math.min(Math.max(v, lo), hi);
  const dist = (a: Point, b: Point) =>
    Math.abs(a.x - b.x) + Math.abs(a.y - b.y);

  let best = farPoint;
  let bestHost: number | null = null;
  let bestDist = dist(near, farPoint);

  for (let i = 0; i < wires.length; i++) {
    if (run.has(i)) continue; // part of the run being replaced
    const wire = wires[i];
    const { start: s, end: e } = wire;
    const touchesFar =
      Point.eq(s, farPoint) ||
      Point.eq(e, farPoint) ||
      interiorContains(wire, farPoint);
    if (!touchesFar) continue;

    // The closest point of this wire to the moved pin: the perpendicular foot,
    // clamped to the wire's extent.
    //
    // Clamping rather than rejecting matters. When the foot falls beyond the
    // wire's end, the wire's nearest endpoint is still a perfectly good place
    // to meet — whereas giving up sends the route all the way back to the
    // pre-move far point, laying its final leg along the very wire it was
    // trying to reach and leaving two collinear segments on top of each other.
    let candidate: Point | null = null;
    if (s.y === e.y && s.x !== e.x) {
      candidate = {
        x: clamp(near.x, Math.min(s.x, e.x), Math.max(s.x, e.x)),
        y: s.y,
      };
    } else if (s.x === e.x && s.y !== e.y) {
      candidate = {
        x: s.x,
        y: clamp(near.y, Math.min(s.y, e.y), Math.max(s.y, e.y)),
      };
    }
    if (!candidate) continue;

    const d = dist(near, candidate);
    if (d < bestDist) {
      bestDist = d;
      best = candidate;
      bestHost = i;
    }
  }

  return { point: best, host: bestHost };
}

// Parse a point key back into coordinates.
function keyPoint(key: string): Point {
  const [x, y] = key.split(",").map(Number);
  return { x, y };
}

// Crossover coordinates for a detour between `from` and `to` along one axis:
// the midpoint of each gap between the things in the way, nearest `from` first.
//
// Halving each gap rather than stepping by some fixed amount is what keeps this
// grid-agnostic — the router snaps to nothing, and a hardcoded step is exactly
// the bug the grid discipline note in `manhattan_router` is about.
function crossovers(from: number, to: number, blockers: number[]): number[] {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  const stops = Array.from(
    new Set([from, to, ...blockers.filter((c) => c > lo && c < hi)])
  ).sort((a, b) => a - b);

  const mids: number[] = [];
  for (let i = 0; i + 1 < stops.length; i++) {
    mids.push((stops[i] + stops[i + 1]) / 2);
  }
  return mids.sort((a, b) => Math.abs(a - from) - Math.abs(b - from));
}

// Everything a route has to miss to keep the nets apart.
interface AvoidSet {
  // Point keys of every pin, at the position it will hold once the plan lands.
  pins: Set<string>;
  // Which of those are already on the net being routed, so passing over one
  // cannot short it on. Empty when the caller does not distinguish them.
  ownPins: Set<string>;
  // Geometry that will belong to another net once the plan lands. Empty when
  // the caller has nothing to say about the rest of the schematic.
  foreign: Array<[Point, Point]>;
}

// Route `from` to `to` without shorting a foreign pin, or another net's wires,
// onto this one.
//
// A pin the route merely passes over is not connected — but a *vertex* on one
// is, and so is a leg that ends there, and the difference is invisible on
// screen. So a route that touches a foreign pin at all is refused: the pin would
// join this net by coincidence of coordinates.
//
// Foreign wires are refused on the connectivity rule rather than on contact: a
// leg may cross one perpendicularly, since a crossing is not a junction, but it
// may not share a point with one, end part-way along one, or run over one.
//
// Three shapes are tried, in increasing order of ugliness:
//
//   1. The router's own choice, which accounts for obstacles.
//   2. The other bend. There are only ever two minimum-bend L-shapes.
//   3. A two-bend detour, crossing over part-way. Both L-shapes can be blocked
//      at once — the natural bend landing on a stationary pin while the other
//      lands on one of the dragged instance's own pins is exactly the case that
//      made this necessary — and then no minimum-bend route exists at all.
//   4. A U-turn, when `from` and `to` are collinear and every shape above
//      collapses back onto the one blocked line.
//
// `pinKeys` is every pin to miss. The two endpoints are exempt: the route is
// *supposed* to reach `to`, which is often a stationary pin, and it leaves from
// the pin that is moving. If nothing clean is found the router's choice stands;
// some pin pairs genuinely have no clean route, and refusing to route would
// break the net for real, which is worse than a short a user can see and undo.
function routeAvoiding(
  router: ManhattanRouter,
  from: Point,
  to: Point,
  avoid: AvoidSet,
  obstacles: model.Bbox[],
  snap: (pt: Point) => Point = (pt) => pt
): { path: Point[]; segments: Array<[Point, Point]> } {
  const forbidden = new Set(avoid.pins);
  forbidden.delete(pointKey(from));
  forbidden.delete(pointKey(to));
  const clear = (path: Point[]) =>
    !pathHits(path, forbidden) && !pathShorts(path, avoid.foreign);

  const route = router.route(from, to, { obstacles });
  if (clear(route.path)) return route;

  const horizontalFirst = route.path.length > 2 && route.path[1].y === from.y;
  const alternate = router.route(from, to, {
    obstacles,
    firstLeg: horizontalFirst ? "vertical" : "horizontal",
  });
  if (clear(alternate.path)) return alternate;

  // Both L-shapes are blocked. Detour, keeping the router's preferred axis
  // first, and stay as close to the direct route as the blockers allow.
  //
  // The crossover is the one coordinate this module invents rather than copies
  // from something the caller already snapped, so it is snapped here — nothing
  // in `Point` carries a grid, and the convention is the producer's to keep.
  //
  // But snapping is subordinate to not shorting: on a coarse grid the snapped
  // crossover can land back on the very pin the detour was avoiding, so the
  // unsnapped crossovers are tried too before giving up. An off-grid bend is a
  // wart the user can see and fix; a pin quietly joined to the wrong net is not.
  // Foreign wires contribute their endpoints, not their spans: a crossover can
  // pass through a foreign wire's middle, so only the places where one ends —
  // and so could be met head-on — narrow the gaps worth trying.
  const blockers = [
    ...Array.from(forbidden, keyPoint),
    ...avoid.foreign.flat(),
  ];
  const detours = (
    first: "horizontal" | "vertical",
    place: (pt: Point) => Point
  ): Point[][] =>
    first === "horizontal"
      ? crossovers(
          from.x,
          to.x,
          blockers.map((p) => p.x)
        ).map((c) => {
          const x = place({ x: c, y: from.y }).x;
          return [from, { x, y: from.y }, { x, y: to.y }, to];
        })
      : crossovers(
          from.y,
          to.y,
          blockers.map((p) => p.y)
        ).map((c) => {
          const y = place({ x: from.x, y: c }).y;
          return [from, { x: from.x, y }, { x: to.x, y }, to];
        });

  const byAxis = (place: (pt: Point) => Point): Point[][] =>
    horizontalFirst
      ? [...detours("horizontal", place), ...detours("vertical", place)]
      : [...detours("vertical", place), ...detours("horizontal", place)];

  // A U-turn, for when `from` and `to` are collinear.
  //
  // Then the straight run between them is the only minimum-bend route there is:
  // both L-shapes are that same line, and so is every crossover above, since
  // each bend they can offer lands back on it. A blocker sitting on the line
  // leaves nothing to fall back to, and the route has to step off it, travel
  // alongside, and come back.
  //
  // How far off comes from the blockers themselves — halfway to each one that
  // is not on the line, nearest first — for the same reason `crossovers` halves
  // gaps rather than stepping by a constant.
  const uTurns = (place: (pt: Point) => Point): Point[][] => {
    const offsets = (line: number, coords: number[]): number[] =>
      Array.from(new Set(coords.filter((c) => c !== line)))
        .map((c) => (line + c) / 2)
        .sort((a, b) => Math.abs(a - line) - Math.abs(b - line));

    if (from.y === to.y) {
      return offsets(
        from.y,
        blockers.map((p) => p.y)
      ).map((c) => {
        const y = place({ x: from.x, y: c }).y;
        return [from, { x: from.x, y }, { x: to.x, y }, to];
      });
    }
    if (from.x === to.x) {
      return offsets(
        from.x,
        blockers.map((p) => p.x)
      ).map((c) => {
        const x = place({ x: c, y: from.y }).x;
        return [from, { x, y: from.y }, { x, y: to.y }, to];
      });
    }
    return [];
  };

  const asIs = (pt: Point) => pt;
  const firstClear = (
    paths: Point[][]
  ): { path: Point[]; segments: Array<[Point, Point]> } | null => {
    for (const path of paths) {
      // A snap can collapse a leg to nothing; drop the duplicate vertex so the
      // plan never carries a zero-length segment into a live schematic.
      const trimmed = path.filter(
        (pt, i) => i === 0 || !Point.eq(pt, path[i - 1])
      );
      if (trimmed.length >= 2 && clear(trimmed)) {
        return { path: trimmed, segments: pathToSegments(trimmed) };
      }
    }
    return null;
  };

  const bent = firstClear([...byAxis(snap), ...byAxis(asIs)]);
  if (bent) return bent;

  // The U-turn is held back for something this net is not already joined to.
  //
  // A route passing over a pin of its *own* net cannot short it on, and the
  // merge splits the span there so the pin keeps an endpoint. Detouring around
  // one buys nothing and costs a wire that visibly wanders.
  const foreignPins = new Set(
    Array.from(forbidden).filter((key) => !avoid.ownPins.has(key))
  );
  if (
    pathHits(route.path, foreignPins) ||
    pathShorts(route.path, avoid.foreign)
  ) {
    const escaped = firstClear([...uTurns(snap), ...uTurns(asIs)]);
    if (escaped) return escaped;
  }
  return route;
}

// Would a route along `path` join anything in `foreign` onto this net?
//
// Asked with the same predicate connectivity itself uses, so the planner cannot
// drift from the extractor's idea of what counts as a junction.
function pathShorts(path: Point[], foreign: Array<[Point, Point]>): boolean {
  if (foreign.length === 0) return false;
  for (let i = 1; i < path.length; i++) {
    const leg = { start: () => path[i - 1], end: () => path[i] };
    for (const [start, end] of foreign) {
      if (segmentsConnect(leg, { start: () => start, end: () => end })) {
        return true;
      }
    }
  }
  return false;
}

// Does `path` touch any of `forbidden` — at a vertex, or part-way along a leg?
function pathHits(path: Point[], forbidden: Set<string>): boolean {
  for (let i = 0; i < path.length; i++) {
    if (i > 0 && forbidden.has(pointKey(path[i]))) return true;
    if (i === 0) continue;
    const a = path[i - 1];
    const b = path[i];
    for (const key of forbidden) {
      const [x, y] = key.split(",").map(Number);
      const pt = { x, y };
      if (interiorContains({ start: a, end: b }, pt)) return true;
    }
  }
  return false;
}

// Map a route onto a run's segments, reusing them in order and creating or
// deleting as the path length requires.
function applyRoute(
  path: Point[],
  run: number[],
  farPoint: Point,
  edits: WireEdit[]
): void {
  if (run.length === 0) return;

  if (path.length < 2) {
    // The moving pin landed on the far point. Nets are built from wire
    // segments, so removing every one of them would silently disconnect two
    // now-coincident pins: keep one zero-length segment as the abutment
    // marker. Dot inference ignores zero-length segments, so it draws
    // nothing.
    edits.push({
      kind: "update",
      index: run[0],
      endpoints: [farPoint, farPoint],
    });
    for (let i = 1; i < run.length; i++) {
      edits.push({ kind: "delete", index: run[i] });
    }
    return;
  }

  // One leg per adjacent pair of path points; at most two, since a
  // minimum-bend orthogonal route has at most one bend.
  const legs: Array<[Point, Point]> = [];
  for (let i = 0; i < path.length - 1; i++) legs.push([path[i], path[i + 1]]);

  for (let i = 0; i < legs.length; i++) {
    if (i < run.length) {
      edits.push({ kind: "update", index: run[i], endpoints: legs[i] });
    } else {
      edits.push({ kind: "create", endpoints: legs[i] });
    }
  }
  for (let i = legs.length; i < run.length; i++) {
    edits.push({ kind: "delete", index: run[i] });
  }
}

// Plan the wire edits for a move.
export function planWireEdits(input: PlanInput): WireEdit[] {
  const {
    wires,
    movingPinKeys,
    staticPinKeys,
    delta,
    obstacles = [],
    selectedWireIndices = new Set<number>(),
    movingPins = [],
    snap,
  } = input;

  const edits: WireEdit[] = [];
  if (wires.length === 0) return edits;
  if (delta.x === 0 && delta.y === 0) return edits;

  // `buildConnectivity` works in terms of accessors, matching the live
  // `WireSegment`; adapt the plain pairs this module takes.
  const conn = buildConnectivity(
    wires.map((wire) => ({ start: () => wire.start, end: () => wire.end }))
  );
  const router = new ManhattanRouter();
  const moved = (pt: Point): Point => Point.add(pt, delta);

  // Every pin a route has to miss: the moving ones at their new homes, since
  // that is where they will be when the plan lands, and the stationary ones
  // where they have always been. A drag that lines a moved pin up with another
  // instance's pin row used to hand the route a bend directly on that pin,
  // shorting it onto the net.
  const pinKeysToAvoid = new Set<string>([
    ...movingPins.map((pin) => pointKey(moved(pin))),
    ...staticPinKeys,
  ]);
  // A run ends at a stationary anchor, a branch — or another moving pin.
  //
  // Walking through a moving pin let one run swallow wire belonging to that
  // pin's own run, and then both laid paths to the same anchor. Stopping
  // there splits the net into chains whose ends are each either moving or
  // fixed, which is what decides between translating and re-routing.
  const isStop = (pt: Point) =>
    staticPinKeys.has(pointKey(pt)) ||
    movingPinKeys.has(pointKey(pt)) ||
    conn.isBranchPoint(pt);

  const groups = components(wires);

  // Which pins does each component touch, and will the gesture replan it?
  const touches = groups.map((group) => ({
    moving: group.some(
      (i) =>
        movingPinKeys.has(pointKey(wires[i].start)) ||
        movingPinKeys.has(pointKey(wires[i].end))
    ),
    anchored: group.some(
      (i) =>
        staticPinKeys.has(pointKey(wires[i].start)) ||
        staticPinKeys.has(pointKey(wires[i].end))
    ),
    selected: group.some((i) => selectedWireIndices.has(i)),
  }));
  const replans = touches.map((t) => t.moving || t.selected);

  // What each component's geometry will be once the plan lands.
  //
  // Routes were planned one component at a time, each blind to the others, so
  // two nets re-routing in the same gesture could lay legs through the same
  // place and come out shorted together. A component the gesture leaves alone
  // is final from the start; a replanned one is recorded as it is planned, and
  // is null until then.
  const geometry: Array<Array<[Point, Point]> | null> = groups.map(
    (group, index) =>
      replans[index]
        ? null
        : group.map((i): [Point, Point] => [wires[i].start, wires[i].end])
  );

  // Record what a component became. Anything its edits did not rewrite keeps
  // the geometry it came in with.
  const settle = (owner: number, group: number[], from: number): void => {
    const rewritten = new Set<number>();
    const ends: Array<[Point, Point]> = [];
    for (let k = from; k < edits.length; k++) {
      const edit = edits[k];
      if (edit.kind === "create") {
        ends.push(edit.endpoints);
        continue;
      }
      rewritten.add(edit.index);
      if (edit.kind === "update") ends.push(edit.endpoints);
    }
    for (const i of group) {
      if (rewritten.has(i)) continue;
      ends.push([wires[i].start, wires[i].end]);
    }
    geometry[owner] = ends;
  };

  // One forward pass. A component can only avoid the ones planned before it,
  // which is a real limitation — the order is arbitrary, so a component with
  // room to manoeuvre can go first and park across the path a later one
  // needed. Planning everything a second time against the first pass's results
  // was tried and moved not one case, because where a route has no clean
  // option left it falls back to the router's choice either way.
  for (let index = 0; index < groups.length; index++) {
    if (!replans[index]) continue;
    const group = groups[index];
    const { anchored: touchesStatic } = touches[index];
    const mark = edits.length;
    const foreign = geometry.flatMap((ends, other) =>
      other === index ? [] : ends ?? []
    );
    // The pins already on this net, where they will end up.
    const ownPins = new Set<string>();
    for (const i of group) {
      for (const end of [wires[i].start, wires[i].end]) {
        const key = pointKey(end);
        if (movingPinKeys.has(key)) ownPins.add(pointKey(moved(end)));
        else if (staticPinKeys.has(key)) ownPins.add(key);
      }
    }
    const route = (from: Point, to: Point) =>
      routeAvoiding(
        router,
        from,
        to,
        { pins: pinKeysToAvoid, ownPins, foreign },
        obstacles,
        snap
      );

    if (!touchesStatic) {
      // Nothing anchors it: translate rigidly and keep its shape.
      for (const i of group) {
        edits.push({
          kind: "update",
          index: i,
          endpoints: [moved(wires[i].start), moved(wires[i].end)],
        });
      }
      settle(index, group, mark);
      continue;
    }

    // Anchored. Re-route the run leaving each moving pin; whatever lies
    // beyond a branch point is someone else's geometry and stays put.
    const taken = new Set<number>();
    // When a run's meeting point slides off the end of the wire it met, the
    // stretch beyond it is left dangling — the junction moved, and nothing
    // shortened the wire behind it. Record what needs trimming; applied once
    // every run has had its say, since several may meet the same wire.
    // `from` is the end the junction slid away from, kept because deciding
    // which end of the wire survives needs it.
    const trims = new Map<number, { meet: Point; from: Point }>();
    const noteTrim = (
      host: number | null,
      farPoint: Point,
      meet: Point
    ): void => {
      if (host === null || Point.eq(meet, farPoint)) return;
      const wire = wires[host];
      // Only when the run was meeting this wire's *end*. Sliding along a wire
      // that carries on past the junction leaves nothing behind.
      if (!Point.eq(wire.start, farPoint) && !Point.eq(wire.end, farPoint)) {
        return;
      }
      // And only if nothing else still holds that end in place.
      const stillHeld = wires.some((other, j) => {
        if (j === host || taken.has(j)) return false;
        return (
          Point.eq(other.start, farPoint) ||
          Point.eq(other.end, farPoint) ||
          interiorContains(other, farPoint)
        );
      });
      if (stillHeld || staticPinKeys.has(pointKey(farPoint))) return;

      // Several runs may meet the same wire; keep the least aggressive trim so
      // every meeting point stays on it.
      const existing = trims.get(host);
      const better =
        !existing ||
        Math.abs(meet.x - farPoint.x) + Math.abs(meet.y - farPoint.y) <
          Math.abs(existing.meet.x - farPoint.x) +
            Math.abs(existing.meet.y - farPoint.y);
      if (better) trims.set(host, { meet, from: farPoint });
    };

    // Trace every run before routing any of them.
    //
    // A meeting point must not target a wire this same gesture is going to
    // move. With two selected instances on one net, the second run used to
    // aim at a segment the first had already claimed, so it was routed to a
    // place that no longer existed once the plan finished — and its pin fell
    // off the net. Tracing first means `taken` names every segment being
    // rewritten before any meeting point is chosen, and the outcome no longer
    // depends on which run happened to go first.
    const traced: Array<{
      run: number[];
      farPoint: Point | null;
      remainder: [Point, Point] | null;
      near: Point;
    }> = [];
    for (const i of group) {
      for (const end of [wires[i].start, wires[i].end]) {
        if (!movingPinKeys.has(pointKey(end))) continue;
        const walk = traceRun(end, wires, group, taken, isStop);
        if (walk.run.length === 0) continue;
        traced.push({ ...walk, near: moved(end) });
      }
    }

    // Does `pt` sit on a wire of this component that stays put? Such a point is
    // connected to the net by that wire alone. `taken` is complete by now, so a
    // wire another run is rewriting does not count as staying put.
    //
    // Only this component's wires count. Stopping on another component's wire
    // does not reach this net — it joins two of them — and the wire may not even
    // stay put, since the same gesture can be re-routing it too.
    const mine = new Set(group);
    const onStationaryWire = (pt: Point): boolean =>
      wires.some((wire, j) => {
        if (!mine.has(j)) return false; // another net; touching it is a short
        if (taken.has(j)) return false; // being rewritten by some run
        if (
          movingPinKeys.has(pointKey(wire.start)) ||
          movingPinKeys.has(pointKey(wire.end))
        ) {
          return false; // this one moves too
        }
        return (
          interiorContains(wire, pt) ||
          Point.eq(wire.start, pt) ||
          Point.eq(wire.end, pt)
        );
      });

    for (const { run, farPoint, remainder, near } of traced) {
      // Re-create whatever lay beyond a mid-span branch: the segment that
      // carried it was consumed by the re-route.
      // Both ends of this chain travel together — from a moving pin to
      // another moving pin, or to nothing at all — so its shape is preserved
      // and it simply translates.
      //
      // A mid-span branch on a moving pin does not mean the whole wire travels
      // with it: the wire can carry on past the pin to something anchored, and
      // translating it whole tows that endpoint away, dropping the anchor off
      // the net. Only then is the wire cut back to the branch — if the stretch
      // beyond it moves too, the shape is preserved and it translates whole.
      if (!farPoint || movingPinKeys.has(pointKey(farPoint))) {
        const anchoredBeyond =
          remainder && !movingPinKeys.has(pointKey(remainder[1]))
            ? remainder
            : null;
        const last = run[run.length - 1];
        for (const j of run) {
          const wire = wires[j];
          if (!anchoredBeyond || j !== last) {
            edits.push({
              kind: "update",
              index: j,
              endpoints: [moved(wire.start), moved(wire.end)],
            });
            continue;
          }
          // Keep the end the run came in by; the branch replaces the other.
          const [branch, onward] = anchoredBeyond;
          const entry = Point.eq(wire.start, onward) ? wire.end : wire.start;
          edits.push({
            kind: "update",
            index: j,
            endpoints: [moved(entry), moved(branch)],
          });
        }

        // Re-attach the stretch beyond the branch. Its near end is the moving
        // pin, so it cannot stay where it was: route it from the pin's new
        // home.
        if (anchoredBeyond) {
          const [branch, onward] = anchoredBeyond;
          const target = moved(branch);
          const { point: meet, host } = meetingPoint(
            target,
            onward,
            wires,
            taken,
            staticPinKeys
          );
          noteTrim(host, onward, meet);
          for (const leg of route(target, meet).segments) {
            edits.push({ kind: "create", endpoints: leg });
          }
        }
        continue;
      }

      // Re-create whatever lay beyond a mid-span branch: the segment that
      // carried it is consumed by the re-route below.
      //
      // If that stretch reaches another moving pin, it cannot simply be put
      // back where it was — its far end is about to move. It gets routed from
      // that pin's new position back to the branch instead. Leaving it behind
      // stranded the pin, since the wire it belonged to had already been
      // claimed by this run and so never got a run of its own.
      if (remainder) {
        const [branch, onward] = remainder;
        if (movingPinKeys.has(pointKey(onward))) {
          const target = moved(onward);
          const { point: branchMeet, host: branchHost } = meetingPoint(
            target,
            branch,
            wires,
            taken,
            staticPinKeys
          );
          noteTrim(branchHost, branch, branchMeet);
          for (const leg of route(target, branchMeet).segments) {
            edits.push({ kind: "create", endpoints: leg });
          }
        } else {
          edits.push({ kind: "create", endpoints: remainder });
        }
      }

      // Note a pin coming to rest on a wire does NOT connect it: the
      // extractor joins an instance pin to a net only when a wire endpoint
      // falls inside the pin's box. So the run is never simply discarded —
      // dropping it would break the net. Where the route degenerates to a
      // point, `applyRoute` keeps a zero-length segment holding the
      // connection together.

      const { point: meet, host } = meetingPoint(
        near,
        farPoint,
        wires,
        taken,
        staticPinKeys
      );
      noteTrim(host, farPoint, meet);

      // Stop the route at the first vertex that already lies on a wire which
      // stays put: reaching that point reaches the net, and every further
      // leg would just retrace wire that is already there.
      //
      // This is what a far point on a stationary *pin* needs. That far point
      // is never relocated — the route has to land on the pin — but it does
      // not have to travel along an existing wire to get there. It used to,
      // laying its last leg over the wires already running to that pin.
      //
      // Except where stopping short would abandon a stationary pin. Reaching a
      // wire that merely *passes through* the pin does not reach the pin: a
      // wire has to end on one for it to be connected. So where the far point
      // is a pin nothing else keeps an endpoint on, this route is the only
      // thing holding it to the net and has to go the whole way.
      const orphansPin =
        staticPinKeys.has(pointKey(meet)) &&
        !wires.some(
          (wire, j) =>
            !taken.has(j) &&
            (Point.eq(wire.start, meet) || Point.eq(wire.end, meet))
        );
      let path = route(near, meet).path;
      for (let k = 1; !orphansPin && k < path.length - 1; k++) {
        if (onStationaryWire(path[k])) {
          path = path.slice(0, k + 1);
          break;
        }
      }
      applyRoute(path, run, path[path.length - 1], edits);
    }

    // Shorten any wire whose end the runs slid away from.
    for (const [wireIndex, { meet, from }] of trims) {
      if (taken.has(wireIndex)) continue; // rewritten by a run anyway
      const wire = wires[wireIndex];
      // The meeting point is interior to the wire; keep the end the runs did
      // not slide away from, which is the one `from` is not. Testing `meet`
      // instead always answered `start`, since the line above has just
      // established that `meet` is neither endpoint — so a wire holding a pin
      // at its `end` was trimmed back onto the junction it was abandoning and
      // the pin came off the net.
      if (Point.eq(wire.start, meet) || Point.eq(wire.end, meet)) continue;
      const keepEnd = Point.eq(wire.start, from) ? wire.end : wire.start;
      edits.push({
        kind: "update",
        index: wireIndex,
        endpoints: [keepEnd, meet],
      });
    }

    // A selected wire the runs did not reach still follows the gesture, but an
    // endpoint on a stationary pin stays where it is. Moving it is what tore
    // selected wires off their connections.
    //
    // Holding one end while the other travels would leave the wire diagonal,
    // so a wire anchored at exactly one end is re-routed between the two,
    // exactly as a run is. Only a wire free at both ends translates.
    for (const i of group) {
      if (taken.has(i) || !selectedWireIndices.has(i)) continue;
      const { start, end } = wires[i];
      const startAnchored = staticPinKeys.has(pointKey(start));
      const endAnchored = staticPinKeys.has(pointKey(end));

      if (startAnchored && endAnchored) continue; // pinned at both ends

      if (!startAnchored && !endAnchored) {
        edits.push({
          kind: "update",
          index: i,
          endpoints: [moved(start), moved(end)],
        });
        continue;
      }

      const anchor = startAnchored ? start : end;
      const travelling = moved(startAnchored ? end : start);
      applyRoute(route(travelling, anchor).path, [i], anchor, edits);
    }

    settle(index, group, mark);
  }

  return edits;
}

// Plan a tidy-up of existing geometry: no move, just re-route each run between
// terminals into a minimum-bend path.
//
// Shares the move planner's decomposition — the same components, the same
// notion of where a net branches — so the standalone cleanup command and a
// drag cannot disagree about what is connected to what. It used to carry its
// own tracer, which counted wire endpoints only and so walked straight through
// a mid-span T, the same defect the drag path had.
//
// Runs with no far point (dangling) are left alone: there is nothing to route
// to, and their shape is the only information about what they were for.
export function planCleanupEdits(input: {
  wires: PlannedWire[];
  pinKeys: Set<string>;
  obstacles?: model.Bbox[];
  // See `PlanInput.snap`: only a detour around a pin needs it.
  snap?: (pt: Point) => Point;
}): { edits: WireEdit[]; runs: number } {
  const { wires, pinKeys, obstacles = [], snap } = input;
  const edits: WireEdit[] = [];
  let runs = 0;
  if (wires.length === 0) return { edits, runs };

  const conn = buildConnectivity(
    wires.map((wire) => ({ start: () => wire.start, end: () => wire.end }))
  );
  const router = new ManhattanRouter();
  const isStop = (pt: Point) =>
    pinKeys.has(pointKey(pt)) || conn.isBranchPoint(pt);

  for (const group of components(wires)) {
    const taken = new Set<number>();
    for (const i of group) {
      for (const end of [wires[i].start, wires[i].end]) {
        if (!pinKeys.has(pointKey(end))) continue;
        const { run, farPoint, remainder } = traceRun(
          end,
          wires,
          group,
          taken,
          isStop
        );
        if (run.length === 0) continue;
        if (remainder) edits.push({ kind: "create", endpoints: remainder });
        if (!farPoint) continue; // dangling; leave its shape alone
        runs++;
        // Tidying a run must not bend it over a third pin, same as a drag.
        const route = routeAvoiding(
          router,
          end,
          farPoint,
          { pins: pinKeys, ownPins: pinKeys, foreign: [] },
          obstacles,
          snap
        );
        applyRoute(route.path, run, farPoint, edits);
      }
    }
  }
  return { edits, runs };
}
